import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './Report.css';
import { API_BASE_URL } from '../config';
import Footer from './Footer';
import RecommendationCategory from './RecommendationCategory';

// Import custom hooks
import { useReportData } from '../hooks/useReportData';
import { useAIRecommendations } from '../hooks/useAIRecommendations';
import { useNextSteps } from '../hooks/useNextSteps';
import { useSavedColleges } from '../hooks/useSavedColleges';

// Import components
import ReportHeader from './Report/ReportHeader';
import ReportNavigation from './Report/ReportNavigation';
import StudentProfile from './Report/StudentProfile';
import NextSteps from './Report/NextSteps';
import SavedColleges from './Report/SavedColleges';
import { AIChatWidget, SaveReportWidget, CTAWidget } from './Report/SidebarWidgets';
import AIChatModal from './Report/AIChatModal';
import NPSWidget from './NPSWidget';

// Import utils and services
import { filterAndSortSchools, renderSchoolLink, scrollToSection } from '../utils/reportHelpers';
import reportService from '../services/reportService';

function Report() {
  const { uuid } = useParams();

  // Core data hooks
  const {
    loading,
    error,
    formData,
    recommendations,
    setRecommendations,
    schoolPreferences,
    studentProfile,
    setStudentProfile,
    updatePreference,
    saveEmail
  } = useReportData(uuid);

  // AI recommendations hook
  const {
    recommendedSchools,
    setRecommendedSchools,
    schoolExplanations,
    setSchoolExplanations,
    loadingAIRecommendations,
    prefetchedSchools,
    setPrefetchedSchools,
    prefetchNextBatch
  } = useAIRecommendations(uuid, formData, recommendations);

  // Email state
  const [emailSaved, setEmailSaved] = useState(false);

  // Next steps hook
  const {
    nextSteps,
    loadingNextSteps,
    nextStepsError,
    refreshNextSteps
  } = useNextSteps(uuid, recommendations, emailSaved, formData?.contactEmail);

  // Saved colleges hook
  const { savedColleges, addToSaved, removeFromSaved } = useSavedColleges(recommendations, schoolPreferences, formData);

  // UI state
  const [showDebugProfile, setShowDebugProfile] = useState(false);
  const [hoveredSchool, setHoveredSchool] = useState(null);
  const [activeSection, setActiveSection] = useState('profile-section');

  // Show counts for categories
  const [reachShowCount, setReachShowCount] = useState(9);
  const [targetShowCount, setTargetShowCount] = useState(9);
  const [safetyShowCount, setSafetyShowCount] = useState(9);
  const [loadingMoreReach, setLoadingMoreReach] = useState(false);
  const [loadingMoreTarget, setLoadingMoreTarget] = useState(false);
  const [loadingMoreSafety, setLoadingMoreSafety] = useState(false);

  // Accordion state
  const [openSections, setOpenSections] = useState({
    profile: false,
    nextSteps: true,
    saved: true,
    userSchools: true,
    reach: true,
    target: true,
    safety: true
  });

  // AI refinement state
  const [showAIRefinement, setShowAIRefinement] = useState(false);
  const [isRefining, setIsRefining] = useState(false);

  // Toggle accordion section
  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle email save
  const handleSaveEmail = async (email) => {
    const success = await saveEmail(email);
    if (success) {
      setEmailSaved(true);
    }
    return success;
  };

  // Handle preference change (wrapper for updatePreference with add to saved logic)
  const handlePreferenceChange = (schoolId, preference, school = null, category = null) => {
    if (preference === 'loved' && school) {
      addToSaved(school, category);
    }
    updatePreference(schoolId, preference);
  };

  // Handle removing from saved
  const handleRemoveFromSaved = (schoolId) => {
    const removed = removeFromSaved(schoolId);
    if (removed) {
      handlePreferenceChange(schoolId, 'neutral');
    }
  };

  // Render preference buttons for a school
  const renderPreferenceButtons = (school, category) => {
    const preference = schoolPreferences[school.id] || 'neutral';

    return (
      <div className="preference-buttons">
        <button
          className={`preference-btn love-btn ${preference === 'loved' ? 'active' : ''}`}
          onClick={() => {
            if (preference === 'loved') {
              handleRemoveFromSaved(school.id);
            } else {
              handlePreferenceChange(school.id, 'loved', school, category);
            }
          }}
          title="Save to My Colleges"
          aria-label="Save to My Colleges"
        >
          ❤️
        </button>
        <button
          className={`preference-btn dismiss-btn ${preference === 'dismissed' ? 'active' : ''}`}
          onClick={() => handlePreferenceChange(school.id, preference === 'dismissed' ? 'neutral' : 'dismissed')}
          title="Not interested"
          aria-label="Dismiss this school"
        >
          ✕
        </button>
      </div>
    );
  };

  // Handler to load more schools for a category
  const handleLoadMore = async (category) => {
    const setLoadingState = category === 'reach' ? setLoadingMoreReach :
                           category === 'target' ? setLoadingMoreTarget :
                           setLoadingMoreSafety;

    const currentCount = category === 'reach' ? reachShowCount :
                        category === 'target' ? targetShowCount :
                        safetyShowCount;

    const setShowCount = category === 'reach' ? setReachShowCount :
                        category === 'target' ? setTargetShowCount :
                        setSafetyShowCount;

    // Check if we have prefetched data for this batch
    const prefetched = prefetchedSchools[category];
    if (prefetched && prefetched.for_count === currentCount + 9) {
      // Use prefetched data instantly
      setRecommendedSchools(prev => ({
        ...prev,
        [category]: prefetched.recommended_schools
      }));

      setSchoolExplanations(prev => ({
        ...prev,
        ...prefetched.school_explanations
      }));

      setShowCount(currentCount + 9);

      // Clear this prefetch and immediately prefetch the NEXT batch in background
      setPrefetchedSchools(prev => ({
        ...prev,
        [category]: null
      }));

      // Prefetch the next batch
      setTimeout(() => {
        prefetchNextBatch(category, currentCount + 9);
      }, 500);

      return;
    }

    // No prefetch available, do normal loading
    setLoadingState(true);

    try {
      const data = await reportService.loadMoreSchools({
        uuid,
        category,
        currentCount,
        formData,
        recommendations
      });

      // Update recommended schools with new IDs
      setRecommendedSchools(prev => ({
        ...prev,
        [category]: data.recommended_schools[category] || []
      }));

      // Update explanations
      if (data.school_explanations) {
        setSchoolExplanations(prev => ({
          ...prev,
          ...data.school_explanations
        }));
      }

      // Increase the show count
      setShowCount(currentCount + 9);
    } catch (error) {
      console.error('Error loading more schools:', error);
    } finally {
      setLoadingState(false);
    }
  };

  // Handle AI filter applied
  const handleFilterApplied = async (filteredRecommendations) => {
    setIsRefining(true);
    setShowAIRefinement(false);

    // Update recommendations locally first for immediate feedback
    // Preserve student_profile in local state as well
    setRecommendations({
      ...filteredRecommendations,
      student_profile: studentProfile
    });

    // Clear prefetched schools since they were fetched with old unfiltered data
    setPrefetchedSchools({
      reach: null,
      target: null,
      safety: null
    });

    try {
      // Preserve student_profile in the filtered recommendations before saving
      const recommendationsWithProfile = {
        ...filteredRecommendations,
        student_profile: studentProfile
      };

      const { rankData } = await reportService.applyAIFilter({
        uuid,
        formData,
        filteredRecommendations: recommendationsWithProfile
      });

      // Update recommended schools with new rankings
      setRecommendedSchools(rankData.recommended_schools);
      setSchoolExplanations(rankData.school_explanations || {});

      // Reset show counts to 9
      setReachShowCount(9);
      setTargetShowCount(9);
      setSafetyShowCount(9);
    } catch (error) {
      console.error('Error applying filter:', error);
    } finally {
      setIsRefining(false);
    }
  };

  // Scroll spy effect
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['profile-section', 'saved-colleges', 'reach-section', 'target-section', 'safety-section', 'next-steps-section'];
      const scrollPosition = window.scrollY + 200;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section) {
          const sectionTop = section.offsetTop;
          if (scrollPosition >= sectionTop) {
            setActiveSection(sections[i]);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="report-container">
        <div className="loading-state">
          <h2>Loading your report...</h2>
          <p>Please wait while we retrieve your personalized recommendations.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !formData) {
    return (
      <div className="report-container">
        <div className="error-state">
          <h2>Report Not Found</h2>
          <p>We couldn't find the report you're looking for. It may have been deleted or the link is invalid.</p>
          <Link to="/" className="back-button">Go to Form</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="report-container">
      {/* Header */}
      <ReportHeader
        savedCollegesCount={savedColleges.length}
        onScrollToSaved={() => scrollToSection('saved-colleges')}
      />

      {/* Debug Profile (hidden by default) */}
      {showDebugProfile && (
        <div style={{
          position: 'fixed',
          top: '60px',
          right: '10px',
          backgroundColor: studentProfile ? '#d4edda' : '#f8d7da',
          padding: '15px',
          border: '3px solid ' + (studentProfile ? '#28a745' : '#dc3545'),
          borderRadius: '8px',
          maxWidth: '350px',
          fontSize: '11px',
          zIndex: 10000,
          maxHeight: '500px',
          overflow: 'auto',
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <strong style={{ fontSize: '14px' }}>
              🐛 DEBUG: Student Profile {studentProfile ? '✅' : '❌'}
            </strong>
            <button
              onClick={() => setShowDebugProfile(false)}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '0 5px'
              }}
            >
              ✕
            </button>
          </div>
          <div style={{ marginBottom: '10px', fontSize: '12px' }}>
            <strong>Report UUID:</strong> {uuid || 'NOT FOUND'}
          </div>
          <div style={{ marginBottom: '10px' }}>
            <a
              href={`${API_BASE_URL}/api/universities/reports/${uuid}/pdf/`}
              download={`college-report-${uuid}.pdf`}
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                backgroundColor: '#059669',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Download PDF Report
            </a>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <button
              onClick={async () => {
                const success = await refreshNextSteps();
                alert(success ? 'Next steps refreshed successfully!' : 'Failed to refresh next steps');
              }}
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                backgroundColor: '#0ea5e9',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: 'pointer',
                border: 'none'
              }}
            >
              🔄 Force Refresh Next Steps
            </button>
          </div>
          {studentProfile ? (
            <pre style={{ margin: '5px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word', backgroundColor: '#fff', padding: '10px', borderRadius: '4px' }}>
              {JSON.stringify(studentProfile, null, 2)}
            </pre>
          ) : (
            <div style={{ color: '#721c24', fontWeight: 'bold' }}>
              ⚠️ NO PROFILE FOUND IN RECOMMENDATIONS
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <ReportNavigation
        activeSection={activeSection}
        savedCollegesCount={savedColleges.length}
        onScrollToSection={scrollToSection}
      />

      {/* Two-column layout */}
      <div className="report-layout-container">
        {/* Main Content */}
        <main className="report-main-content">
          {/* Student Profile */}
          <StudentProfile
            formData={formData}
            isOpen={openSections.profile}
            onToggle={() => toggleSection('profile')}
          />

          {/* Saved Colleges */}
          <SavedColleges
            savedColleges={savedColleges}
            isOpen={openSections.saved}
            onToggle={() => toggleSection('saved')}
            onRemoveFromSaved={handleRemoveFromSaved}
          />

          {/* School Recommendations */}
          {recommendations && recommendations.summary && (
            <section className="report-section school-recommendations">
              <h2>Your Personalized College Recommendations</h2>
              <p className="recommendations-intro">
                Based on your {formData.gpa} GPA
                {formData.satScore && ` and ${formData.satScore} SAT score`}
                {formData.actScore && ` and ${formData.actScore} ACT score`},
                we've identified {recommendations.summary.total_count} schools across three categories:
              </p>

              {/* Reach Schools */}
              {recommendations.reach && recommendations.reach.length > 0 && (
                <RecommendationCategory
                  category="reach"
                  schools={recommendations.reach}
                  savedCollegeIds={new Set(savedColleges.map(sc => sc.id))}
                  aiRecommendedSchoolIds={recommendedSchools.reach}
                  showCount={reachShowCount}
                  isLoadingMore={loadingMoreReach}
                  loadingAIRecommendations={loadingAIRecommendations}
                  filterAndSortSchools={(schools, category) =>
                    filterAndSortSchools(schools, category, schoolPreferences, recommendedSchools)
                  }
                  onLoadMore={handleLoadMore}
                  renderPreferenceButtons={renderPreferenceButtons}
                  renderSchoolLink={renderSchoolLink}
                  schoolPreferences={schoolPreferences}
                  schoolExplanations={schoolExplanations}
                  hoveredSchool={hoveredSchool}
                  setHoveredSchool={setHoveredSchool}
                />
              )}

              {/* Target Schools */}
              {recommendations.target && recommendations.target.length > 0 && (
                <RecommendationCategory
                  category="target"
                  schools={recommendations.target}
                  savedCollegeIds={new Set(savedColleges.map(sc => sc.id))}
                  aiRecommendedSchoolIds={recommendedSchools.target}
                  showCount={targetShowCount}
                  isLoadingMore={loadingMoreTarget}
                  loadingAIRecommendations={loadingAIRecommendations}
                  filterAndSortSchools={(schools, category) =>
                    filterAndSortSchools(schools, category, schoolPreferences, recommendedSchools)
                  }
                  onLoadMore={handleLoadMore}
                  renderPreferenceButtons={renderPreferenceButtons}
                  renderSchoolLink={renderSchoolLink}
                  schoolPreferences={schoolPreferences}
                  schoolExplanations={schoolExplanations}
                  hoveredSchool={hoveredSchool}
                  setHoveredSchool={setHoveredSchool}
                />
              )}

              {/* Safety Schools */}
              {recommendations.safety && recommendations.safety.length > 0 && (
                <RecommendationCategory
                  category="safety"
                  schools={recommendations.safety}
                  savedCollegeIds={new Set(savedColleges.map(sc => sc.id))}
                  aiRecommendedSchoolIds={recommendedSchools.safety}
                  showCount={safetyShowCount}
                  isLoadingMore={loadingMoreSafety}
                  loadingAIRecommendations={loadingAIRecommendations}
                  filterAndSortSchools={(schools, category) =>
                    filterAndSortSchools(schools, category, schoolPreferences, recommendedSchools)
                  }
                  onLoadMore={handleLoadMore}
                  renderPreferenceButtons={renderPreferenceButtons}
                  renderSchoolLink={renderSchoolLink}
                  schoolPreferences={schoolPreferences}
                  schoolExplanations={schoolExplanations}
                  hoveredSchool={hoveredSchool}
                  setHoveredSchool={setHoveredSchool}
                />
              )}
            </section>
          )}

          {/* Next Steps - Now at the bottom */}
          <NextSteps
            isOpen={openSections.nextSteps}
            onToggle={() => toggleSection('nextSteps')}
            uuid={uuid}
            nextSteps={nextSteps}
            loadingNextSteps={loadingNextSteps}
            nextStepsError={nextStepsError}
            emailSaved={emailSaved}
            formData={formData}
            onSaveEmail={handleSaveEmail}
          />

          {/* Actions */}
          <div className="report-actions">
            <Link to={`/?edit=${uuid}`} className="action-button primary">
              Edit Profile
            </Link>
            <Link to="/" className="action-button secondary">
              Start Over
            </Link>
            <button className="action-button secondary" onClick={() => window.print()}>
              Print Report
            </button>
          </div>
        </main>

        {/* Right Sidebar */}
        <aside className="right-sidebar">
          {/* AI Chat Widget */}
          <AIChatWidget
            onStartAIRefinement={() => setShowAIRefinement(true)}
            isRefining={isRefining}
            showAIRefinement={showAIRefinement}
          />

          {/* Save Report Widget */}
          {!formData.contactEmail && !emailSaved && (
            <SaveReportWidget onSaveEmail={handleSaveEmail} />
          )}

          {/* CTA Widget */}
          <CTAWidget />
        </aside>
      </div>

      {/* AI Chat Modal */}
      <AIChatModal
        show={showAIRefinement}
        onClose={() => setShowAIRefinement(false)}
        studentProfile={{
          gpa: formData.gpa,
          satScore: formData.satScore,
          actScore: formData.actScore,
          psatScore: formData.psatScore,
          majors: formData.majors || []
        }}
        schoolCounts={recommendations?.summary}
        recommendations={recommendations}
        onFilterApplied={handleFilterApplied}
        reportUuid={uuid}
        onProfileUpdated={setStudentProfile}
      />

      {/* NPS Widget */}
      <NPSWidget
        reportUuid={uuid}
        onSubmitScore={({ score, comment, reportUuid }) =>
          reportService.submitNPSScore({ score, comment, reportUuid })
        }
      />

      {/* Footer */}
      <Footer
        showDebugToggle={true}
        onVersionClick={() => setShowDebugProfile(!showDebugProfile)}
      />
    </div>
  );
}

export default Report;